import ApplicationController from './application_controller'

// TPK — cascading Step → Type → Reason selects on the admin "open incident"
// form. Only incident_reason_id is submitted; the server derives the incident
// type from the chosen reason. Step and Type are UI filters driven entirely by
// the `rows` referential payload, so the three selects stay consistent and the
// form degrades to full option lists when JavaScript is unavailable.
export default class extends ApplicationController {
  static targets = ['step', 'type', 'reason']

  connect () {
    this.rows = this.parseRows()
    this.syncFromReason()
  }

  // The referential payload is rendered per-request onto the step select
  // (see incident_v2s admin form) — never on the controller root, whose data
  // attributes are evaluated once at ActiveAdmin registration time.
  parseRows () {
    try {
      return JSON.parse(this.stepTarget.dataset.incidentCascadeRows || '[]')
    } catch (error) {
      return []
    }
  }

  stepChanged () {
    this.typeTarget.value = ''
    this.fillTypes()
    this.fillReasons()
  }

  typeChanged () {
    this.fillReasons()
  }

  // On (re)render a reason may already be selected (e.g. error re-render).
  // Align the Step and Type selects to it so the cascade reflects the choice.
  syncFromReason () {
    const reason = this.rows.find(
      (row) => String(row.id) === this.reasonTarget.value
    )

    if (reason) {
      this.stepTarget.value = String(reason.step_id)
      this.fillTypes()
      this.typeTarget.value = String(reason.type_id)
      this.fillReasons()
      this.reasonTarget.value = String(reason.id)
      return
    }

    this.fillTypes()
    this.fillReasons()
  }

  fillTypes () {
    const types = this.uniqueById(
      this.rowsForStep().map((row) => ({ id: row.type_id, name: row.type_name }))
    )
    this.replaceOptions(this.typeTarget, types)
  }

  fillReasons () {
    // Reasonless types carry a single row with id === null (no reason): keep it
    // out of the Reason select so the type stays selectable but reasonless.
    const reasons = this.rowsForStep()
      .filter((row) => this.matchesType(row) && row.id !== null)
      .map((row) => ({ id: row.id, name: row.name }))
    this.replaceOptions(this.reasonTarget, reasons)
    // A reasonless type has no reasons to pick — hide the whole Reason field
    // (label + empty select) instead of showing a blank dropdown.
    this.toggleReasonField(reasons.length > 0)
  }

  toggleReasonField (visible) {
    const wrapper = this.reasonTarget.closest('li')
    if (wrapper) wrapper.style.display = visible ? '' : 'none'
  }

  rowsForStep () {
    const step = this.stepTarget.value
    return this.rows.filter((row) => !step || String(row.step_id) === step)
  }

  matchesType (row) {
    const type = this.typeTarget.value
    return !type || String(row.type_id) === type
  }

  uniqueById (items) {
    const seen = new Map()
    items.forEach((item) => { if (!seen.has(item.id)) seen.set(item.id, item) })
    return Array.from(seen.values())
  }

  replaceOptions (select, items) {
    const previous = select.value
    select.innerHTML = '<option value=""></option>'
    items.forEach((item) => select.add(new Option(item.name, String(item.id))))
    select.value = items.some((item) => String(item.id) === previous) ? previous : ''
  }
}
